import {
  clear,
  createStore,
  delMany,
  entries,
  get,
  set,
  setMany,
  type UseStore,
} from 'idb-keyval';

/**
 * The tile cache that ordinary map browsing fills, and the settings the service
 * worker drives it from. Shared with `src/sw/sw.ts`, so nothing here may reach
 * for the DOM or the Redux store.
 */

/** Tiles picked up while browsing, kept apart from the per-map `tiles-<id>` caches. */
export const BROWSE_CACHE_NAME = 'tiles-browse';

// keys in the default idb-keyval store, which is what the service worker reads
const CONFIG_KEY = 'browseTileCacheConfig';

const TEMPLATES_KEY = 'browseTileCacheTemplates';

const STATS_KEY = 'browseTileCacheStats';

export type TileServeMode =
  | 'network-only'
  | 'network-first'
  | 'cache-first'
  | 'cache-only';

export type BrowseCacheConfig = {
  mode: TileServeMode;
  /** Whether a tile fetched from the network is written to the cache. */
  store: boolean;
  /** Days a tile stays usable; 0 keeps it until the size cap evicts it. */
  maxAgeDays: number;
  /** Ceiling in MB, past which the least recently served tiles go; 0 is no cap. */
  maxSizeMb: number;
};

export const browseCacheDefaults: BrowseCacheConfig = {
  mode: 'network-only',
  store: false,
  maxAgeDays: 30,
  maxSizeMb: 500,
};

export type BrowseCacheStats = { tiles: number; bytes: number };

export type BrowseTileEntry = {
  size: number;
  /** When the tile was last served — the LRU key. */
  at: number;
  /** When the tile came off the network — what its age is measured from. */
  fetchedAt: number;
};

/**
 * Whether the settings give the service worker anything to do. When they don't
 * it leaves tile requests alone entirely rather than passing them through.
 */
export function browseCacheActive(config: BrowseCacheConfig): boolean {
  return config.mode !== 'network-only' || config.store;
}

let indexStore: UseStore | undefined;

// opened on first use: a session that never turns the cache on shouldn't pay
// for the database connection
function getIndexStore(): UseStore {
  indexStore ??= createStore('fm-browse-tiles', 'entries');

  return indexStore;
}

export async function readBrowseCacheConfig(): Promise<BrowseCacheConfig> {
  return {
    ...browseCacheDefaults,
    ...(await get<Partial<BrowseCacheConfig>>(CONFIG_KEY)),
  };
}

export async function writeBrowseCacheConfig(
  config: BrowseCacheConfig,
): Promise<void> {
  await set(CONFIG_KEY, config);
}

export async function readBrowseTileTemplates(): Promise<string[]> {
  return (await get<string[]>(TEMPLATES_KEY)) ?? [];
}

export async function writeBrowseTileTemplates(
  templates: string[],
): Promise<void> {
  await set(TEMPLATES_KEY, templates);
}

export async function readBrowseCacheStats(): Promise<BrowseCacheStats> {
  return (await get<BrowseCacheStats>(STATS_KEY)) ?? { tiles: 0, bytes: 0 };
}

export async function writeBrowseCacheStats(
  stats: BrowseCacheStats,
): Promise<void> {
  await set(STATS_KEY, stats);
}

export async function readBrowseIndex(): Promise<Map<string, BrowseTileEntry>> {
  return new Map(await entries<string, BrowseTileEntry>(getIndexStore()));
}

export async function writeBrowseIndex(
  updated: [string, BrowseTileEntry][],
  removed: string[],
): Promise<void> {
  if (removed.length > 0) {
    await delMany(removed, getIndexStore());
  }

  if (updated.length > 0) {
    await setMany(updated, getIndexStore());
  }
}

export async function clearBrowseCache(): Promise<void> {
  await caches.delete(BROWSE_CACHE_NAME);

  await clear(getIndexStore());

  await writeBrowseCacheStats({ tiles: 0, bytes: 0 });
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Matches every tile URL a layer template can produce, `@Nx` variants included.
 * The service worker has nothing but the request URL to go on, so this is how it
 * tells a map tile from any other cross-origin request.
 */
export function tileTemplateToRegExp(template: string): RegExp {
  const body = template
    .split(/(\{[sxyz]\})/)
    .map((part) =>
      part === '{s}'
        ? '[^./]+'
        : part === '{x}' || part === '{y}'
          ? '-?\\d+'
          : part === '{z}'
            ? '\\d+'
            : escapeRegExp(part),
    )
    .join('');

  return new RegExp(
    // a protocol-relative template is fetched over whatever the page uses;
    // `@Nx` is the hi-DPI variant ScaledTileLayer appends to the URL
    `^${body.startsWith('//') ? `https?:${body}` : body}(?:@\\d+(?:\\.\\d+)?x)?(?:\\?.*)?$`,
  );
}
